import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../core/store/store';
import LanguageSwitcher from './LanguageSwitcher';
import NavbarServerTime from './NavbarServerTime';
import usePageTranslation from '../hooks/usePageTranslation';

interface NavbarProps {
  onSupportClick?: () => void;
  /** Sur la page d’accueil : ouvre la modale de connexion au lieu de renvoyer vers / */
  onLoginClick?: () => void;
  /** Sur la page d’accueil : ouvre la modale d’inscription. */
  onSignupClick?: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ onSupportClick, onLoginClick, onSignupClick }) => {
  const { isAuthenticated } = useSelector((state: RootState) => state.user);
  const navigate = useNavigate();
  const location = useLocation();
  const { t: tHeader } = usePageTranslation('game', 'DashboardHeader');
  const { t: tDash } = usePageTranslation('game', 'Dashboard');
  const { t: tHome } = usePageTranslation('public', 'Home');

  const btnLogin = 'rounded-xl border border-emerald-500/45 bg-emerald-950/25 px-3 py-2 text-sm font-medium text-emerald-100 transition hover:bg-emerald-900/35';
  const btnSignup = 'rounded-xl border border-blue-500/45 bg-blue-950/25 px-3 py-2 text-sm font-medium text-blue-100 transition hover:bg-blue-900/35';
  const isPanelRoute = location.pathname.startsWith('/panel');

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-slate-600/45 bg-gradient-to-b from-slate-700/95 to-slate-900/95 backdrop-blur-md shadow-md shadow-black/25">
        <div className="mx-auto flex min-h-14 max-w-[1680px] flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-2 sm:px-5">
          <Link to={isAuthenticated ? '/dashboard' : '/'} className="flex min-w-0 shrink-0 items-center gap-2.5 hover:opacity-90">
            <img
              src="/images/logo.png"
              alt={tHome('app.name')}
              className="h-9 w-9 rounded-md object-contain ring-1 ring-slate-500/40"
            />
            <div className="min-w-0 hidden sm:block">
              {!isAuthenticated && (
                <p className="text-[9px] uppercase tracking-[0.22em] text-slate-300">{tHome('navbar.eyebrow')}</p>
              )}
              <p className="truncate text-sm font-bold tracking-wide text-white">OPSECOURS WEB</p>
            </div>
          </Link>

          <div className="flex w-full max-w-full flex-none flex-wrap items-center justify-end gap-3 sm:w-auto sm:max-w-[75%] lg:max-w-[70%]">
            {!isAuthenticated ? (
              <>
                <div className="flex w-full flex-wrap items-center justify-end gap-2 sm:w-auto sm:gap-2.5">
                  <nav className="flex flex-wrap items-center justify-end gap-2 sm:gap-2.5" aria-label={tDash('header.sessionNav')}>
                    {onLoginClick ? (
                      <button type="button" onClick={onLoginClick} className={btnLogin}>
                        {tHeader('login')}
                      </button>
                    ) : (
                      <Link to="/" className={btnLogin}>
                        {tHeader('login')}
                      </Link>
                    )}
                    {onSignupClick ? (
                      <button type="button" onClick={onSignupClick} className={btnSignup}>
                        Inscription
                      </button>
                    ) : (
                      <Link to="/" className={btnSignup}>
                        Inscription
                      </Link>
                    )}
                  </nav>
                </div>
              </>
            ) : (
              <div className="flex flex-wrap items-center justify-end gap-3">
                <NavbarServerTime />
                {!isPanelRoute && (
                  <button
                    type="button"
                    onClick={() => navigate('/panel')}
                    className="flex items-center gap-2 rounded-xl border border-slate-500/50 bg-slate-800/60 px-3 py-2 text-sm font-medium text-slate-100 transition hover:bg-slate-700/80"
                  >
                    <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                      />
                    </svg>
                    <span>{tHeader('panel')}</span>
                  </button>
                )}
                {onSupportClick && !isPanelRoute && (
                  <button type="button" onClick={onSupportClick} className="rounded-xl border border-slate-500/50 bg-slate-800/60 px-3 py-2 text-sm font-medium text-slate-100 transition hover:bg-slate-700/80">
                    {tHeader('support')}
                  </button>
                )}
                <LanguageSwitcher />
              </div>
            )}
          </div>
        </div>
      </header>
    </>
  );
};

export default Navbar;
